import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>;
}

export default function SEOHead({
  title = '星辰影院：高清电影 - 星辰影院电影大全 | 最新蓝光4K电影免费在线观看不缓冲',
  description = '星辰影院提供海量高清电影资源，涵盖电影大全、最新电影、蓝光4K画质，免费在线观看不卡顿不缓冲，一站式满足你的观影需求。',
  keywords = '星辰影院,高清电影,电影大全,最新电影,蓝光电影,4K电影,免费在线观看,不缓冲',
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  schemaData,
}: SEOProps) {
  const location = useLocation();
  const baseUrl = 'https://www.xingchencinema.com';
  const currentUrl = canonicalUrl || `${baseUrl}${location.pathname}`;

  const defaultSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '星辰影院',
        url: baseUrl,
        description: '星辰影院提供海量高清电影资源，涵盖电影大全、最新电影、蓝光4K画质，免费在线观看不卡顿不缓冲。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-9999',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京市',
          addressRegion: '朝阳区',
          streetAddress: '影视产业园区A座',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'WebSite',
        name: '星辰影院',
        url: baseUrl,
        potentialAction: {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            urlTemplate: `${baseUrl}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  const mergedSchema = schemaData
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          ...((defaultSchema['@graph'] as unknown[]) || []),
          ...((schemaData['@graph'] as unknown[]) || [schemaData]),
        ],
      }
    : defaultSchema;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={currentUrl} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="星辰影院" />
      <meta property="og:locale" content="zh_CN" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(mergedSchema) }}
      />
    </>
  );
}

export function BreadcrumbSchema({
  items,
}: {
  items: { name: string; url: string }[];
}) {
  const baseUrl = 'https://www.xingchencinema.com';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${baseUrl}${item.url}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}